import { useState, useEffect, useRef } from 'react'
import { IRevenueAgencyFilters, IFilterConfig } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator, EFilterFetchConvertType } from '../types/enum'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

type IUseFiltersRevenueAgencyPage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	revenueAgencyFiltersConfig: IFilterConfig<keyof IRevenueAgencyFilters>[];
	defaultFiltersValue: IRevenueAgencyFilters;
	initialFiltersValue: IRevenueAgencyFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IRevenueAgencyFilters | null)) => (IRevenueAgencyFilters | null)) | IRevenueAgencyFilters | null)) => void;
}

const REVENUE_AGENCY_FILTERS_DEFAULT: IRevenueAgencyFilters = {
	name: ''
}


export const useRevenueAgencyFiltersPage = (): IUseFiltersRevenueAgencyPage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)

	const profile = useAtomValue(state.profileAtom)

	const [searchParams, setSearchParams] = useSearchParams()

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IRevenueAgencyFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const revenueAgencyFiltersConfig: IFilterConfig<keyof IRevenueAgencyFilters>[] = [
		{
			name: 'name',
			label: t(ALL_LANG_KEY.revenue_agency_list_table.column_name),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		}
		// {
		// 	name: 'isSystem',
		// 	label: 'IsSystem',
		// 	type: EFilterType.selectSingle,
		// 	operator: EFilterOperator.isEqual,
		// 	options: IS_SYSTEM_OPTIONS,
		// 	styles: { minWidth: '160px' }
		// },
		// {
		// 	name: 'notes',
		// 	label: 'Notes',
		// 	type: EFilterType.input,
		// 	operator: EFilterOperator.isLike,
		// 	specOperator: EFilterSpecOperator.everywhere,
		// 	styles: { width: '200px' }
		// },
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		revenueAgencyFiltersConfig,
		REVENUE_AGENCY_FILTERS_DEFAULT
	)

	useEffect(() => {
		//console.log('hook.useRevenueAgencyFiltersPage useEffect::START:: ', { userFilters, filter })
		if (!!userFilters) {
			setSend(true)

			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				userFilters,
				revenueAgencyFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				return prev
			})

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			console.log('hook.useRevenueAgencyFiltersPage:: useEffect::END:: ', {
				userFilters,
				filter,
				newFilter,
				newFilterStr
			})
		}
	}, [userFilters])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		revenueAgencyFiltersConfig,
		defaultFiltersValue: REVENUE_AGENCY_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}